import styled from "@emotion/styled";
import { Component, ReactNode } from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";

import { Edit } from "@livechat/design-system-icons";
import {
  Button,
  DesignToken,
  FormField,
  Icon,
  Input,
  SpacingToken,
  Text,
} from "@livechat/design-system-react-components";

import { EventPlace, trackEngagement } from "helpers/analytics";
import { urlContainsPathName } from "helpers/url";
import { isEmail } from "helpers/validate";
import { IStoreState } from "interfaces/storeState";
import { BillingActions, getIsUpdatingBillingInfo } from "store/entities/billing";
import { withVar } from "themes/helpers";

import { SubscriptionRoutes } from "../constants";

interface IOwnProps {
  email: string | null;
}

interface IStateProps {
  isSaving: boolean;
}

interface IDispatchProps {
  onSave(billingAddress: string): void;
}

interface IProps extends IOwnProps, IStateProps, IDispatchProps {}

export class EditInvoiceEmail extends Component<IProps> {
  state = {
    email: this.props.email,
    editing: false,
    emailError: null,
    isSubmitDisabled: true,
  };

  componentDidUpdate(oldProps: IProps): void {
    const { isSaving } = this.props;

    if (oldProps.isSaving && !isSaving) {
      this.handleTurnOffEditing();
      trackEngagement(
        "Changed email for invoices",
        urlContainsPathName(SubscriptionRoutes.Invoices) ? EventPlace.Invoices : EventPlace.AccountDetails
      );
    }
  }

  handleTurnOnEditing = (): void => {
    this.setState({ editing: true });
  };

  handleTurnOffEditing = (): void => {
    this.setState({
      email: this.props.email,
      editing: false,
      emailError: null,
    });
  };

  handleSetInvoiceEmail = (email: string): void => this.setState({ email });

  handleChangeInvoiceEmail = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (!e.target.value) {
      this.setState({
        email: e.target.value,
        emailError: "Invoice email is required",
        isSubmitDisabled: true,
      });
    } else if (!isEmail(e.target.value)) {
      this.setState({
        email: e.target.value,
        emailError: "Invoice email is invalid",
        isSubmitDisabled: true,
      });
    } else {
      this.setState({
        email: e.target.value,
        emailError: null,
        isSubmitDisabled: false,
      });
    }
  };

  handleSaveInvoiceEmail = (event: React.FormEvent<HTMLFormElement>): void => {
    const { onSave } = this.props;
    const { email } = this.state;
    event.preventDefault();

    if (email) {
      onSave(email);
    }
  };

  render(): ReactNode {
    const { email, editing, emailError, isSubmitDisabled } = this.state;
    const { isSaving } = this.props;

    return (
      <StyledWrapper error={!!emailError && editing}>
        <Label>Send invoices to:</Label>
        {editing ? (
          <StyledForm onSubmit={this.handleSaveInvoiceEmail}>
            <InputField error={emailError ?? undefined}>
              <EmailInput
                data-testid="invoice-email-field"
                id="invoiceEmail"
                value={email || ""}
                onChange={this.handleChangeInvoiceEmail}
                type="email"
                error={!!emailError}
                inputSize="compact"
                autoFocus
              />
            </InputField>
            <Button
              data-testid="change-invoice-email-button"
              type="submit"
              kind="primary"
              disabled={isSubmitDisabled || isSaving}
              size="compact"
            >
              Save
            </Button>
            <Button kind="secondary" size="compact" onClick={this.handleTurnOffEditing} disabled={isSaving}>
              Cancel
            </Button>
          </StyledForm>
        ) : (
          <>
            <Text bold>{email}</Text>
            <Button
              kind="plain"
              size="xcompact"
              data-testid="edit-invoice-email-button"
              onClick={this.handleTurnOnEditing}
              icon={<Icon source={Edit} customColor={withVar(DesignToken.ContentBasicPrimary)} size="small" />}
            />
          </>
        )}
      </StyledWrapper>
    );
  }
}

const mapStateToProps = (state: IStoreState): IStateProps => ({
  isSaving: getIsUpdatingBillingInfo(state),
});

const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
  // @ts-expect-error TS(2345)
  onSave: (billingAddress: string) => dispatch(BillingActions.updateBillingInfo({ invoiceEmail: billingAddress })),
});

const StyledWrapper = styled.div<{ error: boolean }>`
  display: flex;
  align-items: ${
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
    (props: any) => (props.error ? "flex-start" : "center")
  };
  flex: 1;
  gap: var(${SpacingToken.Spacing1});
  min-height: var(${SpacingToken.Spacing8});
`;

const Label = styled(Text)`
  line-height: var(${SpacingToken.Spacing8});
`;

const EmailInput = styled(Input)`
  width: 252px;
`;

const InputField = styled(FormField)`
  margin-bottom: 0;
`;

const StyledForm = styled.form`
  display: flex;
  align-items: flex-start;
  flex: 1;
  gap: var(${SpacingToken.Spacing1});
`;

export default connect(mapStateToProps, mapDispatchToProps)(EditInvoiceEmail);
